import React, { useCallback, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Pages } from '@models/Pages'
import { surveyStorage } from '@plugins/storage'
import Button from '@components/Button/Button'
import LocaleSwitcher from './LocaleSwitcher/LocaleSwitcher'
import SocialMedia from './SocialMedia/SocialMedia'
import Speech from '@components/Speech/Speech'

import './HomePage.scss'

import avatarIcon from '@assets/images/home/avatar-icon.png'
import folderIcon from '@assets/images/home/folder-icon.png'
import hatIcon from '@assets/images/home/hat-icon.png'
import magicBallIcon from '@assets/images/home/magic-ball-icon.png'
import depolarisationIcon from '@assets/images/home/icone-depolarisation.png'
import safeBankIcon from '@assets/images/home/safe-bank-icon.png'

import chevronDown from '@assets/images/home/chevron-down.png'

import WithMenu from '@hoc/WithMenu/WithMenu'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { ConsentContext } from '@context/ConsentContext'
import { APP_SLUG, APP_TYPE, isCanadaApp, isLiteraryApp, isQuebecApp, isSanteApp, isUSAApp } from '@models/AppType'
import { PoweredBy } from './PoweredBy/PoweredBy'
import { TopSection } from './TopSection/TopSection'
import { DatagotchiBrandMark } from '@components/DatagotchiBrandMark/DatagotchiBrandMark'
import { HomeDefiJourney } from '@components/Home/HomeDefiJourney'
import { useGotchiCelebration } from '@context/GotchiCelebrationContext'
import { categoryUsesProgressiveVoteModels, getCategory } from '@utils/categoryFlow'
import { slugLooksLikeEque26 } from '@utils/editionSlug'
import { resolveHomeSubTitle } from '@utils/homeSubTitle'

const infoTiles = [
  {
    icon: avatarIcon,
    text: 'avatarText',
  },
  {
    icon: hatIcon,
    text: 'hatText',
  },
  {
    icon: safeBankIcon,
    text: 'safeBankText',
  },
  {
    icon: folderIcon,
    text: 'folderText',
  },
  {
    icon: magicBallIcon,
    text: 'magicBallText',
    byAppType: true,
  },
  {
    icon: depolarisationIcon,
    text: 'depolarisationText',
  },
]

const chevronTiles = [
  {
    text: 'chevronDown1',
  },
  {
    text: 'chevronDown2',
    byAppType: true,
  },
  {
    text: 'chevronDown3',
  },
]

export const HomePage = () => {
  const { t, i18n } = useTranslation('Home')
  const { step, enabled, survey } = useContext(SurveyContext)
  const { hasConsented, checkConsents, consentData } = useContext(ConsentContext)
  const { playDash } = useGotchiCelebration()
  const category = getCategory(survey)
  const homeSubTitle = resolveHomeSubTitle(category, t('subTitle'), i18n.language)
  const isEque26 =
    slugLooksLikeEque26(APP_SLUG) || categoryUsesProgressiveVoteModels(category)
  const isStartSurveyCta = !hasConsented

  const handleClear = () => {
    if (enabled) {
      void surveyStorage.clear().then(() => location.reload())
    }
  }

  useEffect(() => {
    if (!hasConsented) {
      void checkConsents()
    }
  }, [hasConsented, checkConsents])

  const ButtonText = useCallback(() => {
    if (!hasConsented) {
      return <span>{t('startSurvey')}</span>
    }

    if (!consentData) {
      return <span>{t('startSurvey')}</span>
    }

    if (step !== SurveyStep.Sandbox) {
      return <span>{t('resumeSurvey')}</span>
    }

    return <span>{t('trySimulator')}</span>
  }, [consentData, hasConsented, step, t])

  return (
    <div className={`HomePage${isSanteApp ? ' HomePage--sante' : ''}`}>
      <div className="HomePage-Bar" style={isLiteraryApp ? { backgroundColor: '#293436' } : {}}>
        <div className="HomePage-Bar-locale">
          <Link
            to={`/${Pages.Editions}`}
            className={`HomePage-editionsLink is-temporary${isLiteraryApp ? ' BOOKSELLERS' : ''}`}>
            <DatagotchiBrandMark label={t('homeButton', { ns: 'Editions' })} />
          </Link>
          <LocaleSwitcher />
        </div>
        <SocialMedia />
      </div>
      <header className="HomePage-Header">
        {isUSAApp && (
          <div className="HomePage-Header-Speech">
            <Speech propClassName={`HomePage-Header-Speech--${APP_TYPE}`}>{t('homeSpeech')}</Speech>
          </div>
        )}

        <TopSection />
      </header>
      <div className="HomePage-title">
        <h1>{t('title')}</h1>
        {isSanteApp && <p className="HomePage-titleEdition">{t('editionDatagotchiSante')}</p>}
        {!isSanteApp && <h2>{homeSubTitle}</h2>}
      </div>

      <section className="HomePage-content">
        <hr />
        <div className="TopTitle">{t('introductoryText')}</div>
        {(isCanadaApp || isUSAApp || isQuebecApp) && (
          <>
            <hr />
            <PoweredBy />
          </>
        )}
        <div className="InfoTiles">
          {infoTiles.map((tile) => (
            <div key={`info_${tile.text}`}>
              <div className="ImgContainer">
                <img src={tile.icon} alt={t(tile.text)} />
              </div>
              <span>{t(tile.text)}</span>
            </div>
          ))}
        </div>
        {isEque26 ? (
          <HomeDefiJourney className="HomePage-defiCallout" />
        ) : (
          <div className="ChevronTiles">
            {chevronTiles.map((tile, index) => (
              <div key={`chevron_${tile.text}`}>
                <div className="ImgContainer">
                  <img src={chevronDown} alt="" />
                  <div>{`${index + 1}`}</div>
                </div>
                <span>{t(tile.text)}</span>
              </div>
            ))}
          </div>
        )}
        <div className="SecondaryTitle">{t('startSurveyConsent')}</div>
        {!enabled && (
          <p className="HomePage-disabledSurvey" role="status">
            {t('disabledSurvey')}
          </p>
        )}
        <div className="Buttons">
          <Button
            propClassName="start is-multiline"
            to={`/${Pages.Survey}`}
            disabled={!enabled}
            onClick={() => {
              if (isStartSurveyCta) {
                playDash()
              }
            }}>
            <ButtonText />
          </Button>
          <button disabled={!enabled} className="reset-btn" onClick={handleClear}>
            {t('reset')}
          </button>
        </div>

        <hr />
        <footer>
          <small>
            <Link to={`/${Pages.Terms}`}>{t('usagePolicy')}</Link> &nbsp;/&nbsp;{' '}
            <Link to={`/${Pages.Privacy}`}>{t('privacyPolicy')}</Link>
          </small>
          <small>{t('footer')}</small>
          <small>{t('copyright')}</small>
        </footer>
      </section>
    </div>
  )
}

export default WithMenu(HomePage)
